import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Search, Loader2, User, Building2, Landmark, Receipt, Wrench,
  Cog, Plus, ArrowRight, CornerDownLeft, Command as CommandIcon,
  LayoutDashboard, BellRing, Package, UserCog, MessageSquareText, Zap,
  Clock, X,
} from "lucide-react";

// ---- Type registry (add new types by extending this map only) --------------
const TYPE_META = {
  customer: { label: "Customer", icon: User,      chip: "bg-[#39FF14]/10 text-[#39FF14] border-[#39FF14]/25",      ring: "text-[#39FF14]" },
  subsidy:  { label: "Subsidy",  icon: Landmark,  chip: "bg-yellow-500/10 text-yellow-300 border-yellow-500/25",   ring: "text-yellow-300" },
  sale:     { label: "Sale",     icon: Receipt,   chip: "bg-blue-500/10 text-blue-300 border-blue-500/25",          ring: "text-blue-300" },
  machine:  { label: "Machine",  icon: Cog,       chip: "bg-[#39FF14]/5 text-[#F8F9FA] border-[#3A4640]",           ring: "text-[#F8F9FA]" },
  service:  { label: "Service",  icon: Wrench,    chip: "bg-[#1A1F1C] text-[#F8F9FA] border-[#3A4640]",             ring: "text-[#F8F9FA]" },
  action:   { label: "Action",   icon: Zap,       chip: "bg-yellow-500/10 text-yellow-300 border-yellow-500/30",    ring: "text-yellow-300" },
};

// ---- Quick Actions ---------------------------------------------------------
const QUICK_ACTIONS = [
  { id: "act-new-customer", type: "action", title: "New Customer",   subtitle: "Add an individual, business or government client", url: "/customers?new=1", icon: User,       keywords: "new customer add farmer create" },
  { id: "act-new-sale",     type: "action", title: "New Sale",       subtitle: "Record a machine sale and auto-generate an invoice", url: "/sales?new=1",     icon: Receipt,    keywords: "new sale invoice create record" },
  { id: "act-new-subsidy",  type: "action", title: "New Subsidy Case",subtitle: "Open a subsidy application on Subsidy Desk",       url: "/subsidies?new=1", icon: Landmark,   keywords: "new subsidy case scheme apply create" },
  { id: "act-new-service",  type: "action", title: "New Service Job",subtitle: "Log a service / maintenance job card",              url: "/services?new=1",  icon: Wrench,     keywords: "service job card maintenance repair" },
  { id: "act-new-followup", type: "action", title: "New Follow-up",  subtitle: "Schedule a reminder / send SMS",                    url: "/followups?new=1", icon: BellRing,   keywords: "new followup reminder sms whatsapp" },
  { id: "act-new-product",  type: "action", title: "Add Product",    subtitle: "Add machinery / implement to inventory",            url: "/products?new=1",  icon: Package,    keywords: "new product inventory tractor implement" },
  { id: "goto-dashboard",   type: "action", title: "Go to Dashboard", subtitle: "Overview & KPIs",                                   url: "/",                icon: LayoutDashboard, keywords: "dashboard home overview kpi" },
  { id: "goto-customers",   type: "action", title: "Go to Customers", subtitle: "Full customer directory",                          url: "/customers",       icon: User,       keywords: "customers farmers directory list" },
  { id: "goto-inventory",   type: "action", title: "Go to Inventory", subtitle: "Products, stock, catalogue",                       url: "/products",        icon: Package,    keywords: "inventory stock products catalogue" },
  { id: "goto-sales",       type: "action", title: "Go to Sales",     subtitle: "Invoices & sales history",                         url: "/sales",           icon: Receipt,    keywords: "sales invoices history" },
  { id: "goto-subsidies",   type: "action", title: "Go to Subsidy Desk", subtitle: "Applications, permits, tranches",              url: "/subsidies",       icon: Landmark,   keywords: "subsidies desk permit application" },
  { id: "goto-services",    type: "action", title: "Go to Service",   subtitle: "Job cards & maintenance",                          url: "/services",        icon: Wrench,     keywords: "service job cards maintenance" },
  { id: "goto-followups",   type: "action", title: "Go to Follow-ups", subtitle: "Reminders, SMS & WhatsApp queue",                 url: "/followups",       icon: BellRing,   keywords: "followups reminders sms whatsapp" },
];

const ADMIN_ACTIONS = [
  { id: "goto-team",         type: "action", title: "Go to Team",           subtitle: "Manage users & roles",     url: "/users",                     icon: UserCog,          keywords: "team users manage staff sales rep admin" },
  { id: "goto-messages",     type: "action", title: "Go to Messages",       subtitle: "Templates & broadcasts",   url: "/settings/notifications",    icon: MessageSquareText, keywords: "messages sms broadcast templates whatsapp" },
  { id: "goto-security",     type: "action", title: "Go to Security Log",   subtitle: "Audit & auto-lock events", url: "/settings/security-log",     icon: CommandIcon,      keywords: "security log audit lockout" },
];

// ---- Helpers ---------------------------------------------------------------
function escapeRegex(s) { return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }

function Highlight({ text, query }) {
  if (!text) return null;
  const q = (query || "").trim();
  if (!q) return <>{text}</>;
  const re = new RegExp(`(${escapeRegex(q)})`, "ig");
  const parts = String(text).split(re);
  return (
    <>
      {parts.map((p, i) =>
        p.toLowerCase() === q.toLowerCase()
          ? <mark key={i} className="bg-amber-200/80 text-[#F8F9FA] rounded-sm px-0.5">{p}</mark>
          : <span key={i}>{p}</span>
      )}
    </>
  );
}

function filterQuickActions(items, q) {
  const query = (q || "").trim().toLowerCase();
  if (!query) return items;
  return items.filter((a) => {
    const hay = `${a.title} ${a.subtitle || ""} ${a.keywords || ""}`.toLowerCase();
    return query.split(/\s+/).every((tok) => hay.includes(tok));
  });
}

// ---- Recent items (localStorage, per-user, max 5) --------------------------
const RECENTS_MAX = 5;
const RECENTS_KEY_PREFIX = "tzat.commandPalette.recents.";
// Only real modules are worth remembering — quick actions are shortcuts, not "items".
const REMEMBERABLE_TYPES = new Set(["customer", "subsidy", "sale", "machine", "service"]);

function recentsKey(user) {
  return RECENTS_KEY_PREFIX + (user?.email || "anon");
}

function loadRecents(user) {
  try {
    const raw = localStorage.getItem(recentsKey(user));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (_) {
    return [];
  }
}

function saveRecents(user, list) {
  try {
    localStorage.setItem(recentsKey(user), JSON.stringify(list.slice(0, RECENTS_MAX)));
  } catch (_) { /* quota / private mode — silently ignore */ }
}

function pushRecent(user, item) {
  if (!item || !REMEMBERABLE_TYPES.has(item.type)) return;
  const list = loadRecents(user);
  const key = `${item.type}:${item.id}`;
  const filtered = list.filter((it) => `${it.type}:${it.id}` !== key);
  const next = [{
    id: item.id, type: item.type, title: item.title,
    subtitle: item.subtitle, url: item.url, meta: item.meta,
    at: Date.now(),
  }, ...filtered].slice(0, RECENTS_MAX);
  saveRecents(user, next);
}

function clearRecents(user) { try { localStorage.removeItem(recentsKey(user)); } catch (_) { /* noop */ } }

// ---- Component -------------------------------------------------------------
export default function CommandPalette({ open, onOpenChange }) {
  const [query, setQuery] = useState("");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [recents, setRecents] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  // Build the flat, keyboard-navigable list from groups + quick actions.
  const availableActions = useMemo(() => {
    return user?.role === "admin" ? [...QUICK_ACTIONS, ...ADMIN_ACTIONS] : QUICK_ACTIONS;
  }, [user?.role]);

  const flatItems = useMemo(() => {
    const q = query.trim();
    const actionsGroup = { type: "action", label: "Quick Actions",
                           items: filterQuickActions(availableActions, q).slice(0, q ? 6 : 8) };
    const recentsGroup = { type: "recent", label: "Recent", items: recents };
    const list = [];
    // Data groups first when there is a query (they carry real info); recents + quick actions on top when empty.
    if (!q) {
      if (recentsGroup.items.length) list.push(recentsGroup);
      list.push(actionsGroup);
    } else {
      groups.forEach((g) => g.items?.length && list.push(g));
      if (actionsGroup.items.length) list.push(actionsGroup);
    }
    // Flatten with header markers so we can render + navigate correctly.
    const flat = [];
    list.forEach((g) => {
      flat.push({ __header: true, label: g.label, type: g.type });
      g.items.forEach((it) => flat.push({ ...it, __group: g.type }));
    });
    return flat;
  }, [groups, query, availableActions, recents]);

  const selectableIndexes = useMemo(
    () => flatItems.map((it, i) => (it.__header ? -1 : i)).filter((i) => i >= 0),
    [flatItems]
  );

  // ---- Reset when opened / closed
  useEffect(() => {
    if (open) {
      setQuery("");
      setGroups([]);
      setHighlight(0);
      setRecents(loadRecents(user));
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open, user]);

  // ---- Global keyboard shortcut (Ctrl+K / Cmd+K)
  useEffect(() => {
    const onKey = (e) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        onOpenChange?.(!open);
      } else if (e.key === "Escape" && open) {
        onOpenChange?.(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  // ---- Debounced fetch
  const fetchResults = useCallback(async (q) => {
    if (!q || !q.trim()) { setGroups([]); setLoading(false); return; }
    const reqId = ++requestIdRef.current;
    setLoading(true);
    try {
      const { data } = await api.get(`/global-search?q=${encodeURIComponent(q.trim())}&limit=6`);
      if (reqId === requestIdRef.current) setGroups(data?.groups || []);
    } catch (_) {
      if (reqId === requestIdRef.current) setGroups([]);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchResults(query), 180);
    return () => clearTimeout(debounceRef.current);
  }, [query, fetchResults]);

  // ---- Reset highlight when list changes — deterministic: always land on first selectable row.
  useEffect(() => {
    const first = selectableIndexes[0];
    if (typeof first === "number") setHighlight(first);
  }, [flatItems.length, selectableIndexes.length, selectableIndexes[0]]);

  // ---- Scroll highlighted into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${highlight}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [highlight]);

  const selectByIndex = (idx) => {
    let target = idx;
    // Defensive: if the highlight lands on a header (or invalid index), fall back to first selectable row.
    if (!flatItems[target] || flatItems[target].__header) {
      target = selectableIndexes[0];
    }
    const item = flatItems[target];
    if (!item || item.__header) return;
    pushRecent(user, item);
    onOpenChange?.(false);
    if (item.url) navigate(item.url);
  };

  const removeRecent = (e, item) => {
    e.stopPropagation();
    const next = recents.filter((r) => !(r.type === item.type && r.id === item.id));
    setRecents(next);
    saveRecents(user, next);
  };

  const handleClearRecents = () => {
    clearRecents(user);
    setRecents([]);
  };

  const moveHighlight = (dir) => {
    const idxPos = selectableIndexes.indexOf(highlight);
    let next;
    if (idxPos === -1) next = selectableIndexes[0];
    else next = selectableIndexes[(idxPos + dir + selectableIndexes.length) % selectableIndexes.length];
    if (typeof next === "number") setHighlight(next);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); moveHighlight(1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); moveHighlight(-1); }
    else if (e.key === "Enter")   { e.preventDefault(); selectByIndex(highlight); }
    else if (e.key === "Escape")  { e.preventDefault(); onOpenChange?.(false); }
  };

  if (!open) return null;

  const totalResults = flatItems.filter((it) => !it.__header).length;
  const hasQuery = query.trim().length > 0;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center pt-[10vh] px-4 bg-[#0B0F0C]/60 backdrop-blur-sm"
      role="dialog"
      aria-label="Global Search"
      data-testid="command-palette"
      onClick={() => onOpenChange?.(false)}
    >
      <div
        className="w-full max-w-2xl bg-[#121815] rounded-xl shadow-2xl border border-[#3A4640] overflow-hidden animate-in fade-in-0 zoom-in-95"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#3A4640] bg-[#121815]">
          <Search size={18} className="text-[#8A8F98] shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search customers, invoices, subsidies, machines… or run an action"
            autoComplete="off"
            data-testid="command-palette-input"
            className="flex-1 bg-transparent outline-none text-[15px] text-[#F8F9FA] placeholder-stone-400"
          />
          {loading && <Loader2 size={16} className="text-[#8A8F98] animate-spin" />}
          <kbd className="hidden sm:inline-flex items-center gap-1 text-[10px] font-heading uppercase tracking-wider text-[#8A8F98] bg-[#121815] border border-[#3A4640] rounded px-1.5 py-0.5">
            Esc
          </kbd>
        </div>

        {/* Results list */}
        <div ref={listRef} className="max-h-[60vh] overflow-y-auto" data-testid="command-palette-results">
          {totalResults === 0 && hasQuery && !loading && (
            <div className="px-6 py-10 text-center" data-testid="command-palette-empty">
              <div className="text-[#F8F9FA] text-sm mb-1">No results for <b>&ldquo;{query}&rdquo;</b></div>
              <div className="text-[#8A8F98] text-xs">Try a name, TZAT number, invoice, chassis or engine number.</div>
            </div>
          )}

          {flatItems.map((it, idx) => {
            if (it.__header) {
              const isRecentHeader = it.type === "recent" && recents.length > 0;
              return (
                <div key={`h-${idx}`} className="flex items-center justify-between px-4 pt-3 pb-1 text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] bg-[#0B0F0C]/60 border-b border-[#3A4640]">
                  <span className="inline-flex items-center gap-1.5">
                    {it.type === "recent" && <Clock size={11} className="text-[#8A8F98]" />}
                    {it.label}
                  </span>
                  {isRecentHeader && (
                    <button
                      type="button"
                      onClick={handleClearRecents}
                      data-testid="command-palette-clear-recents"
                      className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] hover:text-red-600 transition-colors"
                    >
                      Clear
                    </button>
                  )}
                </div>
              );
            }
            const meta = TYPE_META[it.type] || TYPE_META.action;
            const Icon = it.icon || meta.icon;
            const isHighlighted = idx === highlight;
            const isRecent = it.__group === "recent";
            return (
              <button
                type="button"
                key={`${it.__group || it.type}-${it.type}-${it.id}`}
                data-idx={idx}
                data-testid={`command-palette-result-${it.type}-${it.id}`}
                onMouseEnter={() => setHighlight(idx)}
                onClick={() => selectByIndex(idx)}
                className={`group w-full text-left flex items-center gap-3 px-4 py-2.5 border-b border-stone-50 last:border-0 transition-colors ${
                  isHighlighted ? "bg-[#39FF14]/5" : "hover:bg-[#0B0F0C]"
                }`}
              >
                <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 border ${
                  isHighlighted ? "bg-[#39FF14] text-[#0B0F0C] border-[#39FF14]/40" : `bg-[#121815] ${meta.ring} border-[#3A4640]`
                }`}>
                  <Icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-heading font-semibold text-[#F8F9FA] truncate text-[14px]">
                      <Highlight text={it.title} query={query} />
                    </div>
                    <span className={`inline-flex items-center text-[9px] font-heading uppercase tracking-wider px-1.5 py-0.5 rounded border ${meta.chip}`}>
                      {meta.label}
                    </span>
                    {it.meta?.incomplete && (
                      <span className="text-[9px] uppercase tracking-wider bg-yellow-500/15 text-yellow-200 border border-yellow-500/30 px-1 py-0.5 rounded font-heading">
                        Incomplete
                      </span>
                    )}
                  </div>
                  {it.subtitle && (
                    <div className="text-[12px] text-[#8A8F98] truncate mt-0.5">
                      <Highlight text={it.subtitle} query={query} />
                    </div>
                  )}
                </div>
                {isRecent && (
                  <button
                    type="button"
                    onClick={(e) => removeRecent(e, it)}
                    aria-label="Remove from recent"
                    data-testid={`command-palette-remove-recent-${it.type}-${it.id}`}
                    className="opacity-0 group-hover:opacity-100 text-[#8A8F98] hover:text-red-600 p-1 rounded transition-opacity shrink-0"
                  >
                    <X size={13} />
                  </button>
                )}
                {isHighlighted && !isRecent && (
                  <div className="text-[10px] font-heading uppercase tracking-wider text-[#8A8F98] flex items-center gap-1 shrink-0">
                    <CornerDownLeft size={12} /> Enter
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-[#3A4640] bg-[#0B0F0C] text-[11px] font-heading text-[#8A8F98]">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <kbd className="bg-[#121815] border border-[#3A4640] rounded px-1.5 py-0.5 text-[10px]">↑</kbd>
              <kbd className="bg-[#121815] border border-[#3A4640] rounded px-1.5 py-0.5 text-[10px]">↓</kbd>
              Navigate
            </span>
            <span className="inline-flex items-center gap-1">
              <kbd className="bg-[#121815] border border-[#3A4640] rounded px-1.5 py-0.5 text-[10px]">↵</kbd>
              Open
            </span>
            <span className="inline-flex items-center gap-1">
              <kbd className="bg-[#121815] border border-[#3A4640] rounded px-1.5 py-0.5 text-[10px]">Esc</kbd>
              Close
            </span>
          </div>
          <div className="hidden sm:flex items-center gap-1">
            <CommandIcon size={11} /> Three Zee ERP · Global Search
          </div>
        </div>
      </div>
    </div>
  );
}
